import { useEffect, useState, useMemo, useCallback } from "react";
import { useChatStore, Message, Conversation } from "@/stores/chat.store";
import { useAuthStore } from "@/stores/auth.store";
import { MessageList } from "./message-list";
import { MessageInput } from "./message-input";
import { NewConversationDialog } from "./new-conversation-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamUser {
  _id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  avatar?: string;
}

interface Client {
  _id: string;
  name: string;
  displayName?: string;
  clientCode: string;
  email?: string;
}

type Tab = "team" | "clients" | "groups";

interface ChatPanelProps {
  className?: string;
}

/** Resolve the best display name for a team user */
function userName(u: TeamUser): string {
  return (
    u.displayName?.trim() ||
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.email
  );
}

/** Extract the display name from a populated participant (string id or user object) */
function participantDisplayName(p: unknown): string | null {
  if (!p || typeof p === "string") return null;
  const obj = p as Record<string, unknown>;
  if (obj.displayName) return obj.displayName as string;
  const fn = obj.firstName ?? "";
  const ln = obj.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  if (full) return full;
  if (obj.email) return obj.email as string;
  return null;
}

/** Sort conversations newest-first by lastMessageAt */
function sortConvs(convs: Conversation[]): Conversation[] {
  return [...convs].sort((a, b) => {
    const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : new Date(a.createdAt).getTime();
    const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : new Date(b.createdAt).getTime();
    return tb - ta;
  });
}

/** Compare a participant entry (may be string id or populated object) to a userId string */
function participantMatches(p: unknown, userId: string): boolean {
  if (!p) return false;
  if (typeof p === "string") return p === userId;
  const obj = p as Record<string, unknown>;
  return (
    obj._id?.toString() === userId ||
    obj.id?.toString() === userId
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ChatPanel({ className }: ChatPanelProps) {
  const {
    activeConversationId,
    conversations,
    messages: allMessages,
    setActiveConversation,
    fetchConversations,
    createConversation,
    totalUnread,
    onlineUsers,
    unreadCounts,
  } = useChatStore();
  const { user } = useAuthStore();

  const [tab, setTab] = useState<Tab>("team");
  const [search, setSearch] = useState("");
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [openingConv, setOpeningConv] = useState<string | null>(null); // track loading state per person

  // People data
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  const activeConversation = conversations.find(
    (c) => c._id === activeConversationId,
  );

  // ── Fetch people ────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (tab === "team" && teamUsers.length === 0) {
      setLoadingTeam(true);
      api
        .get<{ data?: TeamUser[]; users?: TeamUser[] } | TeamUser[]>("/users", {
          params: { limit: 100 },
        })
        .then((res) => {
          let data: TeamUser[] = [];
          if (Array.isArray(res.data)) {
            data = res.data;
          } else if (Array.isArray((res as any).data)) {
            data = (res as any).data;
          } else if (Array.isArray((res.data as any)?.users)) {
            data = (res.data as any).users;
          }
          const filtered = data.filter((u) => u._id !== user?.id);
          setTeamUsers(filtered);

          // Fetch initial presence for all team members
          if (filtered.length > 0) {
            api
              .post<{ presence: Record<string, { status: string }> }>(
                "/communication/presence/batch",
                { userIds: filtered.map((u) => u._id) },
              )
              .then((presRes) => {
                const presenceMap = (presRes.data as any)?.presence ?? {};
                Object.entries(presenceMap).forEach(([uid, val]: [string, any]) => {
                  useChatStore.getState()._setOnlineUser(uid, val?.status === "online" || val?.status === "away");
                });
              })
              .catch(() => {});
          }
        })
        .catch(() => {})
        .finally(() => setLoadingTeam(false));
    }
  }, [tab, user?.id]);

  useEffect(() => {
    if (tab === "clients" && clients.length === 0) {
      setLoadingClients(true);
      api
        .get<{ data?: Client[]; clients?: Client[] } | Client[]>(
          "/crm/clients",
          { params: { limit: 100 } },
        )
        .then((res) => {
          let data: Client[] = [];
          if (Array.isArray(res.data)) {
            data = res.data;
          } else if (Array.isArray((res as any).data)) {
            data = (res as any).data;
          } else if (Array.isArray((res.data as any)?.clients)) {
            data = (res.data as any).clients;
          }
          setClients(data);
        })
        .catch(() => {})
        .finally(() => setLoadingClients(false));
    }
  }, [tab]);

  // Clear reply when conversation changes
  useEffect(() => {
    setReplyTo(null);
  }, [activeConversationId]);

  // ── Conversation helpers ─────────────────────────────────────────────────────

  /** Find an existing direct conversation with a given user */
  const findDirectConv = useCallback(
    (targetUserId: string) =>
      conversations.find(
        (c) =>
          c.type === "direct" &&
          c.participants.some((p) => participantMatches(p, targetUserId)) &&
          c.participants.some((p) => participantMatches(p, user?.id ?? "")),
      ),
    [conversations, user?.id],
  );

  /** Find an existing client_channel for a client */
  const findClientConv = useCallback(
    (clientId: string) =>
      conversations.find(
        (c) => c.type === "client_channel" && c.clientId === clientId,
      ),
    [conversations],
  );

  const openOrCreateDirect = useCallback(
    async (target: TeamUser) => {
      // 1. Check local store first (quick path)
      const existing = findDirectConv(target._id);
      if (existing) {
        setActiveConversation(existing._id);
        return;
      }

      setOpeningConv(target._id);
      try {
        // 2. Use dedicated server endpoint — handles get-or-create atomically
        const res = await api.post<{ conversation: Conversation }>(
          "/communication/conversations/direct",
          { otherUserId: target._id },
        );
        const conv = res.data?.conversation ?? (res as any).data;
        if (!conv?._id) return;

        // 3. Add to store conversations if missing, then open via setActiveConversation
        const storeState = useChatStore.getState();
        const alreadyInStore = storeState.conversations.some((c) => c._id === conv._id);
        if (!alreadyInStore) {
          // Directly mutate via fetchConversations to stay in sync
          await storeState.fetchConversations();
        }
        setActiveConversation(conv._id);
      } catch (err) {
        console.error("Failed to open/create direct conversation:", err);
      } finally {
        setOpeningConv(null);
      }
    },
    [findDirectConv, setActiveConversation],
  );

  const openOrCreateClient = useCallback(
    async (client: Client) => {
      const existing = findClientConv(client._id);
      if (existing) {
        setActiveConversation(existing._id);
        return;
      }

      if (!user?.id) return;
      setOpeningConv(client._id);
      try {
        const result = await createConversation({
          type: "client_channel",
          title: `Chat with ${client.displayName || client.name}`,
          participantIds: [user.id],
          clientId: client._id,
          isClientVisible: true,
        });
        if (result?._id) setActiveConversation(result._id);
      } catch (err) {
        console.error("Failed to open/create client conversation:", err);
      } finally {
        setOpeningConv(null);
      }
    },
    [findClientConv, setActiveConversation, createConversation, user?.id],
  );

  // ── Filtered lists ───────────────────────────────────────────────────────────

  const q = search.toLowerCase();

  const filteredTeam = useMemo(
    () =>
      teamUsers.filter((u) => {
        const n = userName(u).toLowerCase();
        return (
          n.includes(q) ||
          (u.email ?? "").toLowerCase().includes(q) ||
          (u.role ?? "").toLowerCase().includes(q)
        );
      }),
    [teamUsers, q],
  );

  const filteredClients = useMemo(
    () =>
      clients.filter(
        (c) =>
          (c.displayName || c.name || "").toLowerCase().includes(q) ||
          (c.clientCode ?? "").toLowerCase().includes(q),
      ),
    [clients, q],
  );

  const groupConversations = useMemo(
    () =>
      sortConvs(
        conversations.filter(
          (c) =>
            c.type === "group" &&
            ((c.title ?? "").toLowerCase().includes(q) ||
              (c.code ?? "").toLowerCase().includes(q)),
        ),
      ),
    [conversations, q],
  );

  // Per-tab unread counts (only count unread for conversations of that type)
  const groupUnread = useMemo(
    () =>
      conversations
        .filter((c) => c.type === "group")
        .reduce((sum, c) => sum + (unreadCounts.get(c._id) || 0), 0),
    [conversations, unreadCounts],
  );

  // For team tab: sort users so those with recent messages appear first
  const sortedTeam = useMemo(() => {
    return filteredTeam.slice().sort((a, b) => {
      const convA = findDirectConv(a._id);
      const convB = findDirectConv(b._id);
      const tA = convA?.lastMessageAt ? new Date(convA.lastMessageAt).getTime() : 0;
      const tB = convB?.lastMessageAt ? new Date(convB.lastMessageAt).getTime() : 0;
      return tB - tA;
    });
  }, [filteredTeam, findDirectConv]);

  // For clients tab: sort clients with recent messages first
  const sortedClients = useMemo(() => {
    return filteredClients.slice().sort((a, b) => {
      const convA = findClientConv(a._id);
      const convB = findClientConv(b._id);
      const tA = convA?.lastMessageAt ? new Date(convA.lastMessageAt).getTime() : 0;
      const tB = convB?.lastMessageAt ? new Date(convB.lastMessageAt).getTime() : 0;
      return tB - tA;
    });
  }, [filteredClients, findClientConv]);

  // ── Header title ─────────────────────────────────────────────────────────────

  const getConvTitle = useCallback(
    (conv: Conversation) => {
      // For direct messages show the OTHER person's name
      if (conv.type === "direct") {
        const other = conv.participants.find(
          (p) => !participantMatches(p, user?.id ?? ""),
        );
        const name = participantDisplayName(other);
        if (name) return name;
        // Fallback: look up in teamUsers
        const otherId =
          typeof other === "string"
            ? other
            : (other as any)?._id?.toString();
        if (otherId) {
          const tu = teamUsers.find((u) => u._id === otherId);
          if (tu) return userName(tu);
        }
        return "Direct Message";
      }
      if (conv.title) return conv.title;
      if (conv.type === "client_channel") return "Client Channel";
      if (conv.type === "job_thread")
        return conv.context?.entityName || "Job Thread";
      return conv.code ?? "Conversation";
    },
    [user?.id, teamUsers],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={cn("flex h-full bg-white", className)}>
      {/* New Group Dialog */}
      <NewConversationDialog
        open={showNewGroup}
        onOpenChange={setShowNewGroup}
      />

      {/* ── Left sidebar ── */}
      <div
        className={cn(
          "flex flex-col border-r w-72 flex-shrink-0",
          activeConversationId && "hidden md:flex",
        )}
      >
        {/* Tabs */}
        <div className="flex border-b text-sm font-medium">
          {(["team", "clients", "groups"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setSearch("");
              }}
              className={cn(
                "flex-1 py-2.5 capitalize transition-colors",
                tab === t
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-800",
              )}
            >
              {t}
              {t === "groups" && groupUnread > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold text-white bg-blue-500 rounded-full">
                  {groupUnread > 99 ? "99+" : groupUnread}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-2 border-b">
          <Input
            placeholder={`Search ${tab}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {tab === "team" && (
            <>
              {loadingTeam ? (
                <Spinner />
              ) : sortedTeam.length === 0 ? (
                <Empty text="No team members found" />
              ) : (
                sortedTeam.map((u) => {
                  const conv = findDirectConv(u._id);
                  const unread = conv
                    ? unreadCounts.get(conv._id) || 0
                    : 0;
                  const isActive =
                    conv && conv._id === activeConversationId;
                  const isOnline = onlineUsers.has(u._id);
                  const loading = openingConv === u._id;
                  return (
                    <PersonRow
                      key={u._id}
                      name={userName(u)}
                      sub={conv ? (conv.lastMessagePreview ?? "") : (u.role ?? "")}
                      avatar={u.avatar}
                      isOnline={isOnline}
                      isActive={!!isActive}
                      isLoading={loading}
                      unread={unread}
                      onClick={() => openOrCreateDirect(u)}
                    />
                  );
                })
              )}
            </>
          )}

          {tab === "clients" && (
            <>
              {loadingClients ? (
                <Spinner />
              ) : sortedClients.length === 0 ? (
                <Empty text="No clients found" />
              ) : (
                sortedClients.map((c) => {
                  const conv = findClientConv(c._id);
                  const unread = conv
                    ? unreadCounts.get(conv._id) || 0
                    : 0;
                  const isActive =
                    conv && conv._id === activeConversationId;
                  const loading = openingConv === c._id;
                  return (
                    <PersonRow
                      key={c._id}
                      name={c.displayName || c.name || "Unknown Client"}
                      sub={conv ? (conv.lastMessagePreview ?? "") : (c.clientCode ?? "")}
                      isOnline={false}
                      isActive={!!isActive}
                      isLoading={loading}
                      unread={unread}
                      onClick={() => openOrCreateClient(c)}
                    />
                  );
                })
              )}
            </>
          )}

          {tab === "groups" && (
            <>
              <div className="p-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowNewGroup(true)}
                >
                  + New Group
                </Button>
              </div>
              {groupConversations.length === 0 ? (
                <Empty text="No groups yet" />
              ) : (
                groupConversations.map((conv) => {
                  const unread = unreadCounts.get(conv._id) || 0;
                  const isActive = conv._id === activeConversationId;
                  return (
                    <PersonRow
                      key={conv._id}
                      name={getConvTitle(conv) ?? "Group"}
                      sub={conv.lastMessagePreview ?? `${conv.participantCount ?? 0} members`}
                      isOnline={false}
                      isActive={isActive}
                      isLoading={false}
                      unread={unread}
                      onClick={() => setActiveConversation(conv._id)}
                      icon="👥"
                    />
                  );
                })
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Message area ── */}
      <div
        className={cn(
          "flex-1 flex flex-col min-w-0",
          !activeConversationId && "hidden md:flex",
        )}
      >
        {activeConversationId && activeConversation ? (
          <>
            {/* Conversation header */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-white shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-8 w-8"
                onClick={() => setActiveConversation(null)}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">
                  {getConvTitle(activeConversation)}
                </p>
                <p className="text-xs text-gray-400">
                  {activeConversation.type === "group"
                    ? `${activeConversation.participantCount ?? 0} members`
                    : activeConversation.type === "client_channel"
                      ? "Client channel"
                      : "Direct message"}
                </p>
              </div>
            </div>

            <MessageList
              conversationId={activeConversationId}
              onReply={(msg) => setReplyTo(msg)}
              conversationType={activeConversation.type}
            />
            <MessageInput
              conversationId={activeConversationId}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              conversationContext={{
                title: getConvTitle(activeConversation),
                type: activeConversation.type,
                recentMessages: (
                  allMessages.get(activeConversationId) ?? []
                ).slice(-10),
                currentUserId: user?.id,
              }}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <span className="text-4xl mb-2 block">💬</span>
              <p className="text-sm">Select someone to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PersonRow({
  name,
  sub,
  avatar,
  isOnline,
  isActive,
  isLoading,
  unread,
  onClick,
  icon,
}: {
  name: string;
  sub: string;
  avatar?: string;
  isOnline: boolean;
  isActive: boolean;
  isLoading: boolean;
  unread: number;
  onClick: () => void;
  icon?: string;
}) {
  const initials = (name || "?")
    .split(" ")
    .map((n) => (n[0] ?? ""))
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-gray-50",
        "hover:bg-gray-50 disabled:opacity-60",
        isActive && "bg-blue-50 hover:bg-blue-50",
      )}
    >
      <div className="relative flex-shrink-0">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-semibold">
            {icon ?? initials}
          </div>
        )}
        {/* Green online dot */}
        {isOnline && (
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span
            className={cn(
              "text-sm truncate",
              unread > 0 ? "font-semibold" : "font-medium",
            )}
          >
            {name}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isOnline && !isActive && (
              <span className="text-[10px] text-green-500 font-medium">
                Online
              </span>
            )}
            {unread > 0 && (
              <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold text-white bg-blue-500 rounded-full">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>
        </div>
        <p
          className={cn(
            "text-xs truncate",
            sub ? "text-gray-500" : "text-gray-400",
          )}
        >
          {isLoading ? "Opening..." : (sub || "No messages yet")}
        </p>
      </div>
    </button>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-sm">
      <span className="text-2xl mb-1">💬</span>
      {text}
    </div>
  );
}
